import { Search } from "common/assets/icons";
import {
  Box,
  Button,
  FormControllerContext,
  FormNumberField,
  FormSelect,
  FormTextField,
} from "common/components";
import { addFlowStepPrefix } from "common/utils";
import {
  FindNetworkElementFlowData,
  FindNetworkElementFlowStep,
  TableFlowStep,
} from "core/api";
import { useTranslation } from "i18n";
import { ReactElement, useContext, useState } from "react";
import { useFormContext } from "react-hook-form";
import { theme } from "styles/theme";
import { MAXIMUM_SEARCH_RESULT_ITEMS } from "../../../../components";
import { Table } from "../Table";
import { FlowStepComponentProps } from "../models";
import { StyledStepContentWrapper } from "../steps.styles";

export type FindNetworkElementProps = FlowStepComponentProps<
  FindNetworkElementFlowStep,
  FindNetworkElementFlowData
>;

/** Finds Inventory step component that allows you to find inventories to assign to a product. */
export function FindNetworkElement({
  control,
  flow,
  data,
  flowStep,
  setValue,
  getValues,
  ...props
}: FindNetworkElementProps): ReactElement {
  const { t } = useTranslation(["common", "action"]);

  const { tableStepConfig, networkElementTypes } = flowStep.config;

  const mergedTableStep = {
    ...flowStep,
    ...data.tableStep,
    config: { ...tableStepConfig },
  } as unknown as TableFlowStep;

  const context = useFormContext();
  const searchTextFieldName = addFlowStepPrefix(flowStep, "searchText");
  const maxItemsFieldName = addFlowStepPrefix(flowStep, "maxTableItems");

  const enteredText: string = context?.watch(searchTextFieldName, "");

  const { evaluateOnChange } = useContext(FormControllerContext);
  const [isLoading, setIsLoading] = useState(false); //

  function validateMaxItems(): boolean | string {
    if (
      getValues(maxItemsFieldName) <= 0 ||
      getValues(maxItemsFieldName) > MAXIMUM_SEARCH_RESULT_ITEMS
    ) {
      return t("search:maxItemsExceedsLimit", {
        limit: MAXIMUM_SEARCH_RESULT_ITEMS,
      });
    } else {
      return true;
    }
  }

  function areFilterValuesValid(): boolean {
    return (
      getValues(searchTextFieldName) !== "" &&
      getValues(maxItemsFieldName) > 0 &&
      getValues(maxItemsFieldName) <= MAXIMUM_SEARCH_RESULT_ITEMS
    );
  }

  const handleSearchButtonClick = () => {
    setIsLoading(true);
    evaluateOnChange?.(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
      <StyledStepContentWrapper>
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "3fr 2fr 2fr 2fr 1fr min-content",
            alignItems: "center",
            gap: theme.spacing(1),
          }}
          data-cy="searchFields"
        >
          <FormTextField
            fieldName={searchTextFieldName}
            control={control}
            label={t("action:findNetworkElementStep.searchText")}
            data-cy="searchText"
            defaultValue={data.searchText}
            onKeyDown={(event) => {
              if (event.key === "Enter" && enteredText !== "") {
                evaluateOnChange?.();
              }
            }}
            validationRules={{
              required: true,
            }}
          />
          <FormSelect
            validValues={networkElementTypes}
            includeEmptyOption={true}
            fieldName={addFlowStepPrefix(flowStep, "networkElementTypeId")}
            required={false}
            control={control}
            defaultValue={data.networkElementTypeId}
            label={t("action:findNetworkElementStep.networkElementType")}
            data-cy="networkElementType"
            disabled={!data.networkElementTypeSelectionEnabled}
          />
          <FormNumberField
            fieldName={maxItemsFieldName}
            control={control}
            label={t("action:findNetworkElementStep.maxTableItems")}
            data-cy="maxTableItems"
            defaultValue={data.maxTableItems}
            disabled={!data.maxTableItemsEnabled}
            allowNegative={false}
            allowLeadingZeros={false}
            fixedDecimalScale
            decimalScale={0}
            validationRules={{
              required: true,
            }}
            customValidation={validateMaxItems}
          />
          <Button
            color="primary"
            variant="contained"
            size="small"
            onClick={handleSearchButtonClick}
            disabled={isLoading || !areFilterValuesValid()}
            loading={isLoading}
            data-cy="searchButton"
            aria-label={t("common:buttons.search")}
          >
            <Search />
          </Button>
        </Box>
        {data.tableStep && (
          <Table
            control={control}
            data={data.tableStep}
            flowStep={mergedTableStep}
            flow={flow}
            setValue={setValue}
            getValues={getValues}
            {...props}
          ></Table>
        )}
      </StyledStepContentWrapper>
    </>
  );
}
